import { memo, ReactNode, useMemo } from 'react';
import { Stack, StackProps, SxProps, Typography, useTheme } from '@mui/material';
import { LongText } from '@dfl/mui-react-common';
import { ReactLink, useSecurity } from '@dfl/react-security';
import { IMedia } from 'modules/common/interfaces';
import { AvatarMedia } from 'components/AvatarMedia';
import NoFoodIcon from '@mui/icons-material/NoFood';
import { useTranslation } from 'react-i18next';

type AvatarNameCellProps = StackProps & {
  link?: string;
  name?: string;
  secondary?: string;
  variant?: 'circular' | 'rounded' | 'square';
  image?: IMedia;
  hideImage?: boolean;
  avatarProps?: SxProps;
  hideLink?: boolean;
  permissions?: string | string[];
  icon?: ReactNode;
  lineClamp?: number;
  nameSx?: SxProps;
  secondarySx?: SxProps;
};

const AvatarNameCell = ({
  link = '/',
  name,
  secondary,
  image,
  variant,
  hideImage,
  lineClamp = 2,
  hideLink,
  permissions,
  avatarProps,
  icon,
  nameSx,
  secondarySx,
  ...props
}: AvatarNameCellProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { hasPermission } = useSecurity();

  const content = useMemo(
    () => (
      <Stack flexDirection={'row'} alignItems={'center'} gap={1.5} {...props}>
        {!hideImage && (
          <AvatarMedia name={name} avatar={image} variant={variant} sx={avatarProps}>
            {icon || <NoFoodIcon fontSize='small' />}
          </AvatarMedia>
        )}

        <Stack flexDirection={'column'} gap={0}>
          {name && <LongText lineClamp={lineClamp} text={props?.translate === 'yes' ? t(name) : name} sx={nameSx} />}
          {secondary && (
            <Typography variant={'caption'} color={theme.palette.mode === 'dark' ? theme.palette.grey[700] : 'text.secondary'} sx={secondarySx}>
              {secondary}
            </Typography>
          )}
        </Stack>
      </Stack>
    ),
    [hideImage, icon, image, name, props, secondary, variant, avatarProps, lineClamp, nameSx, secondarySx, t, theme.palette.grey, theme.palette.mode],
  );

  if (hideLink || (permissions && !hasPermission(permissions))) {
    return content;
  }

  return (
    <ReactLink to={link} underline={'hover'}>
      {content}
    </ReactLink>
  );
};

export default memo(AvatarNameCell);
